import React, { useState } from 'react';
import { InfoIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { DisclosureBadge } from './DisclosureBadge';
import { resolveDisclosures, type DisclosureId, type DisclosureSubjectType } from '../../data/disclosures';
interface DisclosureBadgeGroupProps {
  disclosures: DisclosureId[] | undefined;
  subjectType: DisclosureSubjectType;
  /** Use compact labels — table cells, image overlays. */
  compact?: boolean;
  /**
   * Collapse past this many pills behind a "+N" toggle so multiple disclosures
   * never crowd a card on narrow screens. Multiple disclosures are legitimate
   * (Gifted Product + Affiliate), so we make room for them rather than
   * truncating the information away.
   */
  maxVisible?: number;
  /** Leading info icon. One per GROUP, never one per pill. */
  showIcon?: boolean;
  className?: string;
}

/**
 * Renders every disclosure attached to a piece of content.
 *
 * Renders NOTHING when there are no disclosures. For reviews that means an
 * absent badge signals organic, which keeps the badge meaningful — a pill on
 * every card would become wallpaper.
 */
export const DisclosureBadgeGroup: React.FC<DisclosureBadgeGroupProps> = ({
  disclosures,
  subjectType,
  compact = false,
  maxVisible = 2,
  showIcon = true,
  className = ''
}) => {
  const dk = useDarkClasses();
  const [expanded, setExpanded] = useState(false);
  const resolved = resolveDisclosures(disclosures, subjectType);
  if (resolved.length === 0) return null;
  const visible = expanded ? resolved : resolved.slice(0, maxVisible);
  const hiddenCount = resolved.length - visible.length;
  return <span className={`inline-flex flex-wrap items-center gap-1 ${className}`} aria-label="Commercial disclosure" data-id="element-1484" data-mp="17oev3acz3rj3">
      {showIcon && <InfoIcon className={`h-3 w-3 flex-shrink-0 ${dk.textMuted}`} aria-hidden="true" data-id="element-1485" data-mp="yp1msl0l58zy" />}
      {visible.map(disclosure => <DisclosureBadge key={disclosure.id} disclosure={disclosure} compact={compact} data-id="element-1486" data-mp="k3ma2c7qkhkw" />)}
      {hiddenCount > 0 && <button onClick={event => {
      event.stopPropagation();
      event.preventDefault();
      setExpanded(true);
    }} className={`text-xs px-1.5 py-0.5 rounded-full ${dk.badgeNeutral}`} aria-label={`Show ${hiddenCount} more disclosure${hiddenCount === 1 ? '' : 's'}`} data-id="element-1487" data-mp="o2v4ljalojhi">
          +{hiddenCount}
        </button>}
    </span>;
};
export default DisclosureBadgeGroup;